import { useNavigate, useParams } from "react-router-dom";;
import { useState, useEffect, useCallback, useRef } from "react";
import { MainLayout } from "../components/Layout";
import { StatusBadge, REPAIR_STATUSES, getStatusLabel } from "../components/StatusBadge";
import { repairsAPI, partsAPI, invoicesAPI, reviewsAPI, depositsAPI } from "../lib/api";
import PartsSelectionDialog from "../components/PartsSelectionDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Banknote,
  Calendar,
  Camera,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  Edit,
  Eye,
  EyeOff,
  FileDown,
  FileText,
  FolderOpen,
  Image,
  Images,
  Link,
  Loader2,
  Mail,
  MessageCircle,
  Package,
  PenTool,
  Phone,
  Plus,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  ShoppingCart,
  Smartphone,
  Star,
  Trash2,
  Truck,
  Upload,
  User,
  Wallet,
  Wrench,
  X,
} from "lucide-react";;
import { toast } from "sonner";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { QRCodeSVG } from "qrcode.react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RepairDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [repair, setRepair] = useState(null);
  const [parts, setParts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const fileInputRef = useRef(null);
  const invoiceInputRef = useRef(null);
  const deliveryPhotoInputRef = useRef(null);
  const deliverySigCanvasRef = useRef(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [partsDialogOpen, setPartsDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [editDeviceDialogOpen, setEditDeviceDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoViewDialogOpen, setPhotoViewDialogOpen] = useState(false);
  const [invoicePdfDialogOpen, setInvoicePdfDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Ready status with mandatory invoice popup
  const [readyDialogOpen, setReadyDialogOpen] = useState(false);
  const [readyInvoicePdf, setReadyInvoicePdf] = useState(null);
  const [readyInvoiceNumber, setReadyInvoiceNumber] = useState("");
  const [readyInvoiceAmount, setReadyInvoiceAmount] = useState("");
  const readyInvoiceInputRef = useRef(null);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethod, setPaymentMethod] = useState("card");
  // Deposits (avances)
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositNote, setDepositNote] = useState("");
  const [depositSubmitting, setDepositSubmitting] = useState(false);
  const [depositsExpanded, setDepositsExpanded] = useState(true);

  const [newStatus, setNewStatus] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [technicianNotes, setTechnicianNotes] = useState("");
  const [estimatedHours, setEstimatedHours] = useState("");

  // Rapport d'intervention — nouveaux champs
  const [editImei, setEditImei] = useState("");
  const [editDeviceAspect, setEditDeviceAspect] = useState("");
  const [editAccessories, setEditAccessories] = useState([]);
  const [editAccessoriesNotes, setEditAccessoriesNotes] = useState("");
  const [editWorkDone, setEditWorkDone] = useState("");
  const [editInterventionTypes, setEditInterventionTypes] = useState([]);
  const [editTechnicianComment, setEditTechnicianComment] = useState("");
  
  // Device edit form
  const [editDeviceType, setEditDeviceType] = useState("");
  const [editDeviceBrand, setEditDeviceBrand] = useState("");
  const [editDeviceModel, setEditDeviceModel] = useState("");
  const [editSerialNumber, setEditSerialNumber] = useState("");
  const [editProblemDescription, setEditProblemDescription] = useState("");
  const [editEstimatedCost, setEditEstimatedCost] = useState("");
  
  // Photo upload
  const [photoType, setPhotoType] = useState("before");
  const [photoDescription, setPhotoDescription] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Multiple photos upload
  const [multiplePhotos, setMultiplePhotos] = useState([]);
  const [multiplePhotoDialogOpen, setMultiplePhotoDialogOpen] = useState(false);
  const multipleFileInputRef = useRef(null);
  
  // QR Code upload
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  
  // Invoice PDF upload
  const [invoicePdfPreview, setInvoicePdfPreview] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceAmount, setInvoiceAmount] = useState("");
  
  // Delivery form
  const [deliveryPhotoPreview, setDeliveryPhotoPreview] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [deliveryQrCodeData, setDeliveryQrCodeData] = useState(null);
  const [showDeliveryQrCode, setShowDeliveryQrCode] = useState(false);
  const [signatureQrData, setSignatureQrData] = useState(null);
  const [mobileSignatureData, setMobileSignatureData] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null); // clé du doc en cours d'envoi
  const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);
  const [emailDocToSend, setEmailDocToSend] = useState(null); // { label, url, type }
  const [showSignatureQrCode, setShowSignatureQrCode] = useState(false);
  const [signatureQrPolling, setSignatureQrPolling] = useState(false);
  const signatureQrPollRef = useRef(null);
  const [generatingLabel, setGeneratingLabel] = useState(false);

  // Client edit states
  const [editClientDialogOpen, setEditClientDialogOpen] = useState(false);
  const [editClientName, setEditClientName] = useState("");
  const [editClientPhone, setEditClientPhone] = useState("");
  const [editClientEmail, setEditClientEmail] = useState("");
  const [editClientSync, setEditClientSync] = useState(true);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  
  const [selectedParts, setSelectedParts] = useState([]);
  
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceDiscount, setInvoiceDiscount] = useState(0);
  const [isQuote, setIsQuote] = useState(false);
  
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  
  // Documents (quotes/invoices)
  const [documents, setDocuments] = useState([]);
  
  // Intake receipt
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  
  // Messages/Discussion
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  const generateIntakeReceipt = async () => {
    setGeneratingReceipt(true);
    try {
      const res = await repairsAPI.getIntakeReceipt(id);
      toast.success("Reçu de prise en charge ajouté aux documents");
      fetchData(); // Rafraîchir pour afficher dans l'espace Documents
    } catch (err) {
      toast.error("Erreur lors de la génération du reçu");
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // ── Générer le rapport d'intervention manuellement ──
  const handleToggleDocVisibility = async (docKey, currentVisible) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/repairs/${repair.id}/doc-visibility`,
        { doc_key: docKey, visible: !currentVisible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRepair(prev => ({
        ...prev,
        doc_visibility: { ...(prev.doc_visibility || {}), [docKey]: !currentVisible }
      }));
      toast.success(!currentVisible ? "Document visible côté client ✅" : "Document masqué côté client");
    } catch {
      toast.error("Erreur mise à jour visibilité");
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/repairs/${repair.id}/generate-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Facture ${res.data.invoice.invoice_number} générée ✅`);
      fetchRepair();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur génération facture");
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/repairs/${id}/generate-report`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Rapport généré avec succès !");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération du rapport");
    } finally {
      setGeneratingReport(false);
    }
  };

  // ── Envoyer un document par email au client ──
  const handleSendDocumentByEmail = async (docLabel, docUrl, docType) => {
    if (!repair.client_email) {
      toast.error("Aucun email renseigné pour ce client");
      return;
    }
    setSendingEmail(docType);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/repairs/${id}/send-document-email`,
        { document_type: docType, document_url: docUrl, document_label: docLabel },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`📧 "${docLabel}" envoyé à ${repair.client_email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi de l'email");
    } finally {
      setSendingEmail(null);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/repairs/${id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (err) {
      console.error("Error fetching documents:", err);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/repairs/${id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error("Veuillez écrire un message");
      return;
    }
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/repairs/${id}/messages`, 
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Message envoyé au client");
      setNewMessage("");
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      console.log("Fetching repair data for id:", id);
      const repairRes = await repairsAPI.get(id);
      console.log("Repair loaded:", repairRes.data);
      const partsRes = await partsAPI.list();
      console.log("Parts loaded:", partsRes.data?.length);
      const photosRes = await repairsAPI.getPhotos(id);
      console.log("Photos loaded:", photosRes.data?.length);
      setRepair(repairRes.data);
      setParts(partsRes.data);
      setPhotos(photosRes.data || []);
      setDiagnosis(repairRes.data.diagnosis || "");
      setTechnicianNotes(repairRes.data.technician_notes || "");
      setEstimatedHours(repairRes.data.estimated_duration_hours || "");
      setSelectedParts(repairRes.data.parts_used || []);
      // Set device edit fields
      setEditDeviceType(repairRes.data.device_type || "");
      setEditDeviceBrand(repairRes.data.device_brand || "");
      setEditDeviceModel(repairRes.data.device_model || "");
      setEditSerialNumber(repairRes.data.serial_number || "");
      setEditProblemDescription(repairRes.data.problem_description || "");
      setEditEstimatedCost(repairRes.data.estimated_cost || "");
      // Rapport d'intervention
      setEditImei(repairRes.data.imei || repairRes.data.serial_number || "");
      setEditDeviceAspect(repairRes.data.device_aspect || "");
      setEditAccessories(repairRes.data.accessories_received || []);
      setEditAccessoriesNotes(repairRes.data.accessories_notes || "");
      setEditWorkDone(repairRes.data.work_done || "");
      setEditInterventionTypes(repairRes.data.intervention_types || []);
      setEditTechnicianComment(repairRes.data.technician_comment || "");
    } catch (err) {
      console.error("Error fetching repair data:", err);
      toast.error("Erreur lors du chargement");
      navigate("/repairs");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
    fetchMessages();
    fetchDocuments();
  }, [fetchData]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Remove part from repair
  const handleRemovePart = async (partId, source, partName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir retirer "${partName}" de cette réparation ?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/repairs/${id}/parts`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { part_id: partId, source: source }
      });
      toast.success(`Pièce "${partName}" retirée`);
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(msg);
    }
  };

  // Photo upload handler
  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = async () => {
    if (!photoPreview) return;
    setUpdating(true);
    try {
      await repairsAPI.uploadPhoto(id, {
        repair_id: id,
        photo_type: photoType,
        image_data: photoPreview,
        description: photoDescription
      });
      toast.success("Photo ajoutée");
      setPhotoDialogOpen(false);
      setPhotoPreview(null);
      setPhotoDescription("");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUpdating(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    try {
      await repairsAPI.deletePhoto(photoId);
      toast.success("Photo supprimée");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Multiple photos upload handlers
  const handleMultiplePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newPhotos = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    
    setMultiplePhotos((prev) => [...prev, ...newPhotos]);
  };

  const removeMultiplePhoto = (index) => {
    setMultiplePhotos((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleMultiplePhotoUpload = async () => {
    if (multiplePhotos.length === 0) {
      toast.error("Veuillez sélectionner au moins une photo");
      return;
    }
    
    setUpdating(true);
    try {
      const formData = new FormData();
      multiplePhotos.forEach((photo) => {
        formData.append("photos", photo.file);
      });
      formData.append("photo_type", photoType);
      
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/repairs/${id}/photos/multiple`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      
      toast.success(`${multiplePhotos.length} photo(s) ajoutée(s)`);
      setMultiplePhotoDialogOpen(false);
      setMultiplePhotos([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUpdating(false);
    }
  };

  // QR Code upload handlers
  const generateQrCode = async () => {
    setQrCodeLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/repairs/${id}/mobile-upload-token`,
        null,
        {
          params: { photo_type: photoType },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setQrCodeData(res.data);
      setQrCodeDialogOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération du QR code");
    } finally {
      setQrCodeLoading(false);
    }
  };

  // Generate QR code for delivery photos
  const generateDeliveryQrCode = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/repairs/${id}/mobile-upload-token`,
        null,
        {
          params: { photo_type: "after" },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setDeliveryQrCodeData(res.data);
      setShowDeliveryQrCode(true);
      toast.info("Scannez le QR code avec votre téléphone pour ajouter des photos");
    } catch (err) {
      toast.error("Erreur lors de la génération du QR code");
    }
  };

  // Refresh photos uploaded via QR code
  const refreshDeliveryPhotosFromQr = async () => {
    try {
      const token = localStorage.getItem("token");
      // Fetch photos with type "after" for this repair
      const res = await axios.get(
        `${API_URL}/api/repairs/${id}/photos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter only "after" photos that came from mobile upload
      const mobilePhotos = (res.data || []).filter(p => 
        p.photo_type === "after" && p.source === "mobile_upload"
      );
      
      if (mobilePhotos.length > 0) {
        // Add mobile photos to deliveryPhotos if not already present
        const existingIds = deliveryPhotos.map(p => p.id);
        const newPhotos = mobilePhotos
          .filter(p => !existingIds.includes(p.id))
          .map(p => ({
            id: p.id,
            data: `${API_URL}${p.image_url}`,
            name: "Photo mobile",
            fromQr: true
          }));
        
        if (newPhotos.length > 0) {
          setDeliveryPhotos(prev => [...prev, ...newPhotos]);
          toast.success(`${newPhotos.length} nouvelle(s) photo(s) reçue(s) via QR code!`);
        } else {
          toast.info("Aucune nouvelle photo trouvée");
        }
      } else {
        toast.info("Aucune photo uploadée via QR code pour le moment");
      }
    } catch (err) {
      console.error("Error refreshing photos:", err);
      toast.error("Erreur lors du rafraîchissement des photos");
    }
  };

  // Generate QR code for mobile signature
  const generateSignatureQrCode = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/repairs/${id}/mobile-signature-token`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSignatureQrData(res.data);
      setShowSignatureQrCode(true);
      toast.info("QR code généré — donnez le téléphone au client pour qu'il signe");
      // Start polling for signature
      startSignaturePolling(res.data.token);
    } catch (err) {
      toast.error("Erreur lors de la génération du QR code de signature");
    }
  };

  const startSignaturePolling = (token) => {
    setSignatureQrPolling(true);
    if (signatureQrPollRef.current) clearInterval(signatureQrPollRef.current);
    signatureQrPollRef.current = setInterval(async () => {
      try {
        const authToken = localStorage.getItem("token");
        const res = await axios.get(
          `${API_URL}/api/repairs/${id}/mobile-signature-status`,
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        if (res.data.signed && res.data.signature_data) {
          clearInterval(signatureQrPollRef.current);
          setSignatureQrPolling(false);
          setShowSignatureQrCode(false);
          // Stocker la signature dans un state dédié (fiable, pas de dépendance au canvas)
          setMobileSignatureData(res.data.signature_data);
          // Vider le canvas manuel pour éviter la confusion
          if (deliverySigCanvasRef.current) {
            deliverySigCanvasRef.current.clear();
          }
          toast.success("✅ Signature reçue du client !");
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 2000);
  };

  const stopSignaturePolling = () => {
    if (signatureQrPollRef.current) {
      clearInterval(signatureQrPollRef.current);
      signatureQrPollRef.current = null;
    }
    setSignatureQrPolling(false);
    setShowSignatureQrCode(false);
    setSignatureQrData(null);
  };
  const handleDeliveryPhotoAdd = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setDeliveryPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeDeliveryPhoto = (photoId) => {
    setDeliveryPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  // Invoice PDF handlers
  const handleInvoicePdfSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Veuillez sélectionner un fichier PDF");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvoicePdfPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInvoicePdfUpload = async () => {
    if (!invoicePdfPreview || !invoiceNumber || !invoiceAmount) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    setUpdating(true);
    try {
      await repairsAPI.uploadInvoicePdf(id, {
        repair_id: id,
        invoice_data: invoicePdfPreview,
        invoice_number: invoiceNumber,
        amount: parseFloat(invoiceAmount)
      });
      toast.success("Facture ajoutée - Email envoyé au client");
      setInvoicePdfDialogOpen(false);
      setInvoicePdfPreview(null);
      setInvoiceNumber("");
      setInvoiceAmount("");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    } finally {
      setUpdating(false);
    }
  };

  const handleInvoicePdfDelete = async () => {
    if (!window.confirm("Supprimer cette facture ?")) return;
    try {
      await repairsAPI.deleteInvoicePdf(id);
      toast.success("Facture supprimée");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // ── Deposits handlers ──────────────────────────────────────────
  const handleAddDeposit = async () => {
    const amount = parseFloat(depositAmount);
    if (!depositAmount || isNaN(amount) || amount <= 0) {
      toast.error("Montant invalide");
      return;
    }
    setDepositSubmitting(true);
    try {
      await depositsAPI.add(id, { amount, note: depositNote || "Avance client" });
      toast.success(`Avance de ${amount.toFixed(2)}€ ajoutée`);
      setDepositDialogOpen(false);
      setDepositAmount("");
      setDepositNote("");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'ajout de l'avance");
    } finally {
      setDepositSubmitting(false);
    }
  };

  const handleDeleteDeposit = async (depositId, amount) => {
    if (!window.confirm(`Supprimer l'avance de ${amount.toFixed(2)}€ ?`)) return;
    try {
      await depositsAPI.delete(id, depositId);
      toast.success("Avance supprimée");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Delivery handlers
  const handleDeliveryPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setDeliveryPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearDeliverySignature = () => {
    if (deliverySigCanvasRef.current) {
      deliverySigCanvasRef.current.clear();
    }
    setMobileSignatureData(null);
  };

  const handleDelivery = async () => {
    // Bloquer si facture non réglée
    if (repair?.invoice_amount && !repair?.invoice_paid) {
      const remaining = repair?.remaining_balance;
      if (remaining !== null && remaining > 0) {
        toast.error(`Impossible de livrer — Solde restant : ${remaining.toFixed(2)}€`);
        return;
      }
    }
    // Check if we have at least one photo (either single or multiple)
    if (!deliveryPhotoPreview && deliveryPhotos.length === 0) {
      toast.error("Veuillez prendre au moins une photo de l'appareil");
      return;
    }
    // Accepter soit la signature canvas manuelle, soit la signature mobile reçue
    const hasCanvasSignature = deliverySigCanvasRef.current && !deliverySigCanvasRef.current.isEmpty();
    if (!hasCanvasSignature && !mobileSignatureData) {
      toast.error("Veuillez faire signer le client");
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      // Priorité : signature mobile reçue, sinon canvas manuel
      const signatureData = mobileSignatureData || deliverySigCanvasRef.current.toDataURL("image/png");
      
      // Get the main photo (either single or first from multiple)
      const mainPhotoData = deliveryPhotoPreview || deliveryPhotos[0]?.data;
      
      // Convert base64 to blob for file upload
      const photoBlob = await fetch(mainPhotoData).then(r => r.blob());
      const sigBlob = await fetch(signatureData).then(r => r.blob());
      
      const formData = new FormData();
      formData.append("photo", photoBlob, "delivery_photo.jpg");
      formData.append("signature", sigBlob, "signature.png");
      formData.append("delivery_notes", deliveryNotes);
      
      const deliverRes = await axios.post(`${API_URL}/api/repairs/${id}/deliver`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      // Le rapport PDF est sauvegardé automatiquement dans l'espace Documents
      if (deliverRes.data?.report_pdf) {
        toast.success("Rapport d'intervention disponible dans les Documents");
      }
      
      // Upload additional photos if any
      if (deliveryPhotos.length > 1) {
        for (let i = 1; i < deliveryPhotos.length; i++) {
          try {
            await axios.post(
              `${API_URL}/api/repairs/${id}/photos`,
              {
                image_data: deliveryPhotos[i].data,
                photo_type: "after",
                description: "Photo de livraison"
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (photoErr) {
            console.error("Error uploading additional photo:", photoErr);
          }
        }
      }
      
      toast.success("Réparation livrée avec succès !");
      setDeliveryDialogOpen(false);
      setDeliveryPhotoPreview(null);
      setDeliveryPhotos([]);
      setDeliveryNotes("");
      setShowDeliveryQrCode(false);
      setDeliveryQrCodeData(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la livraison");
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    
    // Block direct change to "delivered" - must use delivery modal with signature
    if (newStatus === "delivered") {
      toast.error("Pour passer au statut 'Livré', utilisez le bouton 'Livrer au client' avec photo et signature obligatoires.");
      setStatusDialogOpen(false);
      return;
    }
    
    // Block direct change to "ready" - must use invoice popup
    if (newStatus === "ready") {
      setStatusDialogOpen(false);
      setReadyDialogOpen(true);
      return;
    }
    
    setUpdating(true);
    try {
      await repairsAPI.update(id, { status: newStatus });
      toast.success("Statut mis à jour");
      setStatusDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setUpdating(false);
    }
  };

  // Handle status "ready" with mandatory invoice
  const handleReadyInvoiceSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Accept PDF files - check both extension and type
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        toast.error("Veuillez sélectionner un fichier PDF");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log("PDF file read successfully, length:", reader.result?.length);
        setReadyInvoicePdf(reader.result);
      };
      reader.onerror = () => {
        console.error("Error reading PDF file:", reader.error);
        toast.error("Erreur lors de la lecture du fichier PDF");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReadyWithInvoice = async () => {
    if (!readyInvoicePdf || !readyInvoiceNumber || !readyInvoiceAmount) {
      toast.error("Veuillez remplir tous les champs de la facture");
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      console.log("Uploading invoice PDF...");
      console.log("Data being sent:", {
        invoice_number: readyInvoiceNumber,
        amount: parseFloat(readyInvoiceAmount),
        invoice_data_length: readyInvoicePdf?.length || 0
      });
      
      // First upload the invoice
      const invoiceResponse = await axios.post(
        `${API_URL}/api/repairs/${id}/invoice-pdf`,
        {
          invoice_data: readyInvoicePdf,
          invoice_number: readyInvoiceNumber,
          amount: parseFloat(readyInvoiceAmount)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Invoice uploaded:", invoiceResponse.data);
      
      // Then change status to ready
      console.log("Changing status to ready...");
      await repairsAPI.update(id, { status: "ready" });
      console.log("Status changed successfully");
      
      toast.success("Facture enregistrée et appareil marqué comme prêt");
      setReadyDialogOpen(false);
      setReadyInvoicePdf(null);
      setReadyInvoiceNumber("");
      setReadyInvoiceAmount("");
      
      // Refresh data
      console.log("Refreshing data...");
      await fetchData();
      console.log("Data refreshed successfully");
    } catch (err) {
      console.error("Error in handleReadyWithInvoice:", err);
      console.error("Error response:", err.response?.data);
      // Show detailed error message
      let errorMsg = "Erreur lors de l'enregistrement";
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMsg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          // Pydantic validation errors
          errorMsg = err.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  // Handle marking invoice as paid
  const handleMarkAsPaid = async () => {
    if (!paymentDate || !paymentMethod) {
      toast.error("Veuillez remplir la date et le mode de paiement");
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/repairs/${id}/mark-paid`,
        {
          payment_date: paymentDate,
          payment_method: paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Facture marquée comme payée");
      setPaymentDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelRepair = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_URL}/api/repairs/${id}/cancel`,
        null,
        {
          params: { reason: cancelReason },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Réparation annulée");
      setCancelDialogOpen(false);
      setCancelReason("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'annulation");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteRepair = async () => {
    setUpdating(true);
    try {
      await repairsAPI.delete(id);
      toast.success("Réparation supprimée");
      navigate("/repairs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDetails = async () => {
    setUpdating(true);
    try {
      const data = {
        diagnosis: diagnosis || null,
        technician_notes: technicianNotes || null,
        estimated_duration_hours: estimatedHours ? parseInt(estimatedHours) : null,
        // Rapport d'intervention
        imei: editImei || null,
        device_aspect: editDeviceAspect || null,
        accessories_received: editAccessories,
        accessories_notes: editAccessoriesNotes || null,
        work_done: editWorkDone || null,
        intervention_types: editInterventionTypes,
        technician_comment: editTechnicianComment || null,
      };
      await repairsAPI.update(id, data);
      toast.success("Détails mis à jour");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateDevice = async () => {
    setUpdating(true);
    try {
      const data = {
        device_type: editDeviceType,
        device_brand: editDeviceBrand,
        device_model: editDeviceModel,
        serial_number: editSerialNumber || null,
        problem_description: editProblemDescription,
        estimated_cost: editEstimatedCost ? parseFloat(editEstimatedCost) : null,
      };
      // Si l'IMEI n'est pas encore saisi, pré-remplir avec le N° de série
      if (!editImei && editSerialNumber) {
        data.imei = editSerialNumber;
        setEditImei(editSerialNumber);
      }
      await repairsAPI.update(id, data);
      toast.success("Informations de l'appareil mises à jour");
      setEditDeviceDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const handleOpenEditClient = () => {
    setEditClientName(repair.client_name || "");
    setEditClientPhone(repair.client_phone || "");
    setEditClientEmail(repair.client_email || "");
    setEditClientSync(true);
    setEditClientDialogOpen(true);
  };

  const handleUpdateClientInfo = async () => {
    if (!editClientName.trim() || !editClientPhone.trim()) {
      toast.error("Le nom et le téléphone sont obligatoires");
      return;
    }
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(
        `${API_URL}/api/repairs/${id}/client-info`,
        {
          client_name: editClientName.trim(),
          client_phone: editClientPhone.trim(),
          client_email: editClientEmail.trim() || null,
          sync_to_client: editClientSync,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { synced_repairs_count } = res.data;
      if (editClientSync && synced_repairs_count > 0) {
        toast.success(`Infos client mises à jour et synchronisées sur ${synced_repairs_count + 1} réparation(s)`);
      } else if (editClientSync) {
        toast.success("Infos client mises à jour et fiche client synchronisée");
      } else {
        toast.success("Infos client mises à jour sur cette réparation uniquement");
      }
      setEditClientDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const handleAddParts = async () => {
    if (selectedParts.length === 0) return;
    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const partsToAdd = selectedParts.map(p => ({
        part_id: p.part_id,
        quantity: p.quantity
      }));
      
      const response = await axios.post(
        `${API_URL}/api/repairs/${id}/add-parts-smart`,
        partsToAdd,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { parts_added, parts_ordered, parts_pending_quote, quote_accepted, message } = response.data;
      
      if (!quote_accepted && parts_pending_quote && parts_pending_quote.length > 0) {
        // Devis pas encore accepté
        const needsOrder = parts_pending_quote.filter(p => p.needs_order);
        if (needsOrder.length > 0) {
          toast.info(
            `${parts_pending_quote.length} pièce(s) enregistrée(s). ${needsOrder.length} pièce(s) devront être commandée(s) après acceptation du devis.`,
            { duration: 5000 }
          );
        } else {
          toast.success(`${parts_pending_quote.length} pièce(s) enregistrée(s). Elles seront prélevées du stock après acceptation du devis.`);
        }
      } else {
        if (parts_ordered && parts_ordered.length > 0) {
          toast.warning(`${parts_ordered.length} pièce(s) en rupture de stock - ajoutée(s) aux commandes`);
        }
        if (parts_added && parts_added.length > 0) {
          toast.success(`${parts_added.length} pièce(s) ajoutée(s) depuis le stock`);
        }
      }
      
      setPartsDialogOpen(false);
      setSelectedParts([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateInvoice = async () => {
    const validItems = invoiceItems.filter(function(i) { return i.description && i.unit_price > 0; });
    if (validItems.length === 0) {
      toast.error("Ajoutez au moins un article");
      return;
    }
    setUpdating(true);
    try {
      await invoicesAPI.create({
        repair_id: id,
        items: validItems,
        discount: parseFloat(invoiceDiscount) || 0,
        is_quote: isQuote,
      });
      toast.success(isQuote ? "Devis créé" : "Facture créée");
      setInvoiceDialogOpen(false);
      setInvoiceItems([]);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateReview = async () => {
    setUpdating(true);
    try {
      await reviewsAPI.create({
        repair_id: id,
        rating: reviewRating,
        comment: reviewComment || null,
      });
      toast.success("Avis ajouté");
      setReviewDialogOpen(false);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setUpdating(false);
    }
  };

  const addPartToSelection = (partId) => {
    const part = parts.find(function(p) { return p.id === partId; });
    if (!part) return;
    
    const existing = selectedParts.find(function(p) { return p.part_id === partId; });
    if (existing) {
      setSelectedParts(selectedParts.map(function(p) {
        if (p.part_id === partId) {
          return { ...p, quantity: p.quantity + 1 };
        }
        return p;
      }));
    } else {
      setSelectedParts([...selectedParts, {
        part_id: part.id,
        part_name: part.name,
        quantity: 1,
        unit_price: part.unit_price,
      }]);
    }
  };

  const removePartFromSelection = (partId) => {
    setSelectedParts(selectedParts.filter(function(p) { return p.part_id !== partId; }));
  };

  const addInvoiceItem = (type = "part") => {
    setInvoiceItems([...invoiceItems, { description: "", quantity: 1, unit_price: 0, type: type }]);
  };

  const addLaborItem = () => {
    // Add labor item if not already present
    const hasLabor = invoiceItems.some(item => item.type === "labor");
    if (!hasLabor) {
      setInvoiceItems([...invoiceItems, { description: "Main d'œuvre", quantity: 1, unit_price: 0, type: "labor" }]);
    }
  };

  const updateInvoiceItem = (index, field, value) => {
    const updated = [...invoiceItems];
    if (field === "quantity" || field === "unit_price") {
      updated[index][field] = parseFloat(value) || 0;
    } else {
      updated[index][field] = value;
    }
    setInvoiceItems(updated);
  };

  const removeInvoiceItem = (index) => {
    setInvoiceItems(invoiceItems.filter(function(_, i) { return i !== index; }));
  };

  const calculateInvoiceTotal = () => {
    let sum = 0;
    for (let i = 0; i < invoiceItems.length; i++) {
      sum += invoiceItems[i].quantity * invoiceItems[i].unit_price;
    }
    return sum - (parseFloat(invoiceDiscount) || 0);
  };

  const calculatePartsTotal = () => {
    if (!repair || !repair.parts_used) return 0;
    let sum = 0;
    for (let i = 0; i < repair.parts_used.length; i++) {
      sum += repair.parts_used[i].quantity * repair.parts_used[i].unit_price;
    }
    return sum;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  if (!repair) return null;

  const partsUsed = repair.parts_used || [];
  const statusHistory = repair.status_history || [];

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="repair-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/repairs")} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="font-chivo text-2xl font-bold text-foreground font-mono">
                {repair.ticket_number}
              </h1>
              {/* Banniere RDV livraison */}
              {repair.pickup_appointment && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-violet-500/15 border border-violet-500/40 text-sm">
                  <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
                  <span className="text-violet-300 font-medium">RDV recuperation :</span>
                  <span className="text-white font-bold">{repair.pickup_appointment.date}</span>
                  {repair.pickup_appointment.time_slot && (
                    <span className="text-violet-300">
                      — {{ morning: "Matin (9h–12h)", afternoon: "Après-midi (14h–17h)", evening: "Fin de journée (17h–19h)" }[repair.pickup_appointment.time_slot] || repair.pickup_appointment.time_slot}
                    </span>
                  )}
                </div>
              )}
              <StatusBadge status={repair.status} />
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Créé le {new Date(repair.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div className="flex gap-2">
            {repair.status !== "delivered" && repair.status !== "cancelled" && (
              <Button 
                onClick={() => setCancelDialogOpen(true)} 
                variant="outline"
                className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
              >
                <Ban className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
            <Button 
              onClick={() => setDeleteDialogOpen(true)} 
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
            <Button 
              onClick={() => setStatusDialogOpen(true)} 
              data-testid="change-status-btn"
              className="bg-primary hover:bg-primary/90"
            >
              Changer le statut
            </Button>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Info */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-chivo flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Client
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenEditClient}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{repair.client_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{repair.client_phone}</span>
                </div>
                {repair.client_email && (
                  <div className="flex items-center gap-3 col-span-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{repair.client_email}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Device Info */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-chivo flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-primary" />
                  Appareil
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setEditDeviceDialogOpen(true)}
                  data-testid="edit-device-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Type</p>
                    <p className="font-medium capitalize">{repair.device_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Marque / Modèle</p>
                    <p className="font-medium">{repair.device_brand} {repair.device_model}</p>
                  </div>
                  {repair.serial_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">N° de série</p>
                      <p className="font-medium font-mono text-sm">{repair.serial_number}</p>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Problème signalé</p>
                  <p className="bg-secondary/30 p-3 rounded-md">{repair.problem_description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Diagnostic & Rapport d'Intervention */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-chivo flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-primary" />
                  Diagnostic & Rapport d'Intervention
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">Ces informations apparaîtront sur le rapport remis au client lors de la livraison.</p>
              </CardHeader>
              <CardContent className="space-y-5">

                {/* Ligne IMEI + Aspect */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">IMEI</Label>
                    <Input
                      value={editImei}
                      onChange={(e) => setEditImei(e.target.value)}
                      placeholder="Ex: 353936443018344"
                      className="bg-secondary/50 font-mono text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Aspect de l'appareil</Label>
                    <select
                      value={editDeviceAspect}
                      onChange={(e) => setEditDeviceAspect(e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-border bg-secondary/50 text-sm text-foreground"
                    >
                      <option value="">— Sélectionner —</option>
                      <option value="neuf">Neuf</option>
                      <option value="bon_etat">Bon état</option>
                      <option value="usage">Usagé</option>
                      <option value="endommage">Endommagé</option>
                    </select>
                  </div>
                </div>

                {/* Accessoires reçus */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Accessoires reçus avec l'appareil</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "cable", label: "Câble DATA" },
                      { key: "chargeur", label: "Chargeur" },
                      { key: "coque", label: "Coque / Protection" },
                      { key: "sim", label: "Trappe SIM" },
                      { key: "boite", label: "Boîte d'origine" },
                      { key: "autre", label: "Autre" },
                    ].map(({ key, label }) => (
                      <label key={key} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                        editAccessories.includes(key)
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-secondary/30 text-muted-foreground hover:border-primary/40"
                      }`}>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={editAccessories.includes(key)}
                          onChange={(e) => {
                            if (e.target.checked) setEditAccessories(prev => [...prev, key]);
                            else setEditAccessories(prev => prev.filter(a => a !== key));
                          }}
                        />
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                          editAccessories.includes(key) ? "border-primary bg-primary" : "border-border"
                        }`}>
                          {editAccessories.includes(key) && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        {label}
                      </label>
                    ))}
                  </div>
                  {editAccessories.includes("autre") && (
                    <Input
                      value={editAccessoriesNotes}
                      onChange={(e) => setEditAccessoriesNotes(e.target.value)}
                      placeholder="Préciser les accessoires..."
                      className="bg-secondary/50 text-sm mt-1"
                    />
                  )}
                </div>

                {/* Diagnostic */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Défauts constatés / Diagnostic</Label>
                  <Textarea
                    value={diagnosis}
                    onChange={(e) => setDiagnosis(e.target.value)}
                    placeholder="Ex: Charnière — défaut mécanique, écran fissuré..."
                    data-testid="diagnosis-input"
                    className="bg-secondary/50"
                    rows={2}
                  />
                </div>

                {/* Type d'intervention */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notre intervention a consisté en</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "diagnostic", label: "Diagnostic" },
                      { key: "reparation", label: "Réparation" },
                      { key: "test_complet", label: "Test & vérification" },
                      { key: "nettoyage", label: "Nettoyage" },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          if (editInterventionTypes.includes(key))
                            setEditInterventionTypes(prev => prev.filter(t => t !== key));
                          else
                            setEditInterventionTypes(prev => [...prev, key]);
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          editInterventionTypes.includes(key)
                            ? "bg-primary text-white border-primary"
                            : "bg-secondary/30 text-muted-foreground border-border hover:border-primary/40"
                        }`}
                      >
                        {editInterventionTypes.includes(key) ? "✓ " : ""}{label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Travaux effectués */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Travaux effectués (détail)</Label>
                  <Textarea
                    value={editWorkDone}
                    onChange={(e) => setEditWorkDone(e.target.value)}
                    placeholder={"Ex:\nRemplacement module afficheur\nRemplacement batterie interne\nTest complet"}
                    className="bg-secondary/50 font-mono text-sm"
                    rows={4}
                  />
                </div>

                {/* Commentaire technicien */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Commentaire technicien (visible sur rapport)</Label>
                  <Textarea
                    value={editTechnicianComment}
                    onChange={(e) => setEditTechnicianComment(e.target.value)}
                    placeholder="Ex: Reçu avec câble DATA sans chargeur"
                    data-testid="technician-notes-input"
                    className="bg-secondary/50"
                    rows={2}
                  />
                </div>

                {/* Notes internes */}
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes internes (non imprimées)</Label>
                  <Textarea
                    value={technicianNotes}
                    onChange={(e) => setTechnicianNotes(e.target.value)}
                    placeholder="Notes internes non visibles par le client..."
                    className="bg-secondary/50"
                    rows={2}
                  />
                </div>

                <Button
                  onClick={handleUpdateDetails}
                  disabled={updating}
                  data-testid="save-details-btn"
                  className="bg-primary hover:bg-primary/90"
                >
                  {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer le rapport
                </Button>
              </CardContent>
            </Card>

            {/* Parts Used */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-chivo">Pièces</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setPartsDialogOpen(true)}
                  data-testid="add-parts-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Pièces en attente de devis */}
                {repair?.pending_parts_request && repair.pending_parts_request.length > 0 && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-md">
                    <p className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Pièces en attente d'acceptation du devis
                    </p>
                    <ul className="text-sm space-y-1">
                      {repair.pending_parts_request.map((part, idx) => (
                        <li key={idx} className="flex justify-between items-center group">
                          <span>{part.part_name} (×{part.quantity})</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${part.needs_order ? "bg-amber-500/20 text-amber-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                              {part.needs_order ? "À commander" : "En stock"}
                            </span>
                            <button
                              onClick={() => handleRemovePart(part.part_id, "pending_parts_request", part.part_name)}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                              title="Retirer cette pièce"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Pièces commandées (en attente de réception) */}
                {repair?.parts_ordered && repair.parts_ordered.length > 0 && (
                  <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-md">
                    <p className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Pièces en commande
                    </p>
                    <ul className="text-sm space-y-1">
                      {repair.parts_ordered.map((part, idx) => (
                        <li key={idx} className="flex justify-between items-center group">
                          <span>{part.part_name} (×{part.quantity})</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              part.status === "received" ? "bg-emerald-500/20 text-emerald-400" :
                              part.status === "ordered" ? "bg-blue-500/20 text-blue-400" :
                              "bg-amber-500/20 text-amber-400"
                            }`}>
                              {part.status === "received" ? "Reçu" : part.status === "ordered" ? "En transit" : "À commander"}
                            </span>
                            {(part.status === "to_order" || part.status === "pending") && (
                              <button
                                onClick={() => handleRemovePart(part.id, "parts_ordered", part.part_name)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                                title="Retirer cette pièce"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Pièces utilisées (en stock) */}
                {partsUsed.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Pièces utilisées (prélevées du stock)
                    </p>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Pièce</th>
                          <th>Qté</th>
                          <th>Prix</th>
                          <th>Total</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {partsUsed.map((part, idx) => (
                          <tr key={idx} className="group">
                            <td>{part.part_name}</td>
                            <td>{part.quantity}</td>
                            <td>{part.unit_price.toFixed(2)}€</td>
                            <td>{(part.quantity * part.unit_price).toFixed(2)}€</td>
                            <td className="w-10">
                              <button
                                onClick={() => handleRemovePart(part.part_id, "parts_used", part.part_name)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                                title="Retirer et restaurer le stock"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {/* Message si aucune pièce */}
                {partsUsed.length === 0 && 
                 (!repair?.pending_parts_request || repair.pending_parts_request.length === 0) &&
                 (!repair?.parts_ordered || repair.parts_ordered.length === 0) && (
                  <p className="text-muted-foreground text-center py-4">Aucune pièce sélectionnée</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Tracking Link */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-chivo flex items-center gap-2">
                  <Link className="w-5 h-5 text-primary" />
                  Lien de suivi client
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Partagez ce lien avec le client pour qu'il puisse suivre sa réparation.
                </p>
                <div className="flex gap-2">
                  <Input 
                    readOnly 
                    value={window.location.origin + "/suivi/" + repair.ticket_number}
                    className="bg-secondary/50 text-xs font-mono"
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={function() {
                      navigator.clipboard.writeText(window.location.origin + "/suivi/" + repair.ticket_number);
                      toast.success("Lien copié !");
                    }}
                    data-testid="copy-tracking-link-btn"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={function() {
                    window.open("/suivi/" + repair.ticket_number, "_blank");
                  }}
                  data-testid="open-tracking-btn"
                >
                  <Link className="w-4 h-4 mr-2" />
                  Voir la page de suivi
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-chivo">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => setPhotoDialogOpen(true)}
                  data-testid="add-photo-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Ajouter une photo
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => setMultiplePhotoDialogOpen(true)}
                  data-testid="add-multiple-photos-btn"
                >
                  <Images className="w-4 h-4 mr-2" />
                  Ajouter plusieurs photos
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start text-blue-500 border-blue-500/50 hover:bg-blue-500/10" 
                  onClick={generateQrCode}
                  disabled={qrCodeLoading}
                  data-testid="qr-code-btn"
                >
                  {qrCodeLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <QrCode className="w-4 h-4 mr-2" />
                  )}
                  QR Code upload mobile
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => { 
                    setIsQuote(true); 
                    setInvoiceItems([
                      { description: "", quantity: 1, unit_price: 0, type: "part" },
                      { description: "Main d'œuvre", quantity: 1, unit_price: 0, type: "labor" }
                    ]); 
                    setInvoiceDialogOpen(true); 
                  }}
                  data-testid="create-quote-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Créer un devis
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start" 
                  onClick={() => setInvoicePdfDialogOpen(true)}
                  data-testid="upload-invoice-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Joindre facture PDF
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" 
                  onClick={generateIntakeReceipt}
                  disabled={generatingReceipt}
                  data-testid="generate-receipt-btn"
                >
                  {generatingReceipt ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  {repair.intake_receipt_pdf ? "Reçu de prise en charge ✓" : "Générer le reçu"}
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled={generatingLabel}
                  onClick={async () => {
                    setGeneratingLabel(true);
                    try {
                      await repairsAPI.generateLabel(id);
                      toast.success("Étiquette Dymo ajoutée aux documents");
                      fetchData();
                    } catch (e) {
                      toast.error("Erreur génération étiquette");
                    } finally {
                      setGeneratingLabel(false);
                    }
                  }}
                >
                  {generatingLabel
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <QrCode className="w-4 h-4 mr-2" />
                  }
                  {repair.label_pdf ? "Étiquette Dymo ✓" : "Générer l'étiquette Dymo"}
                </Button>
                {repair.status === "ready" && (() => {
                  const hasInvoice = !!repair.invoice_amount;
                  const isPaid = repair.invoice_paid;
                  const isDeposited = repair.remaining_balance !== null && repair.remaining_balance <= 0;
                  const canDeliver = !hasInvoice || isPaid || isDeposited;
                  return (
                    <div className="space-y-1">
                      <Button
                        className={`w-full justify-start text-white ${canDeliver ? "bg-emerald-500 hover:bg-emerald-600" : "bg-red-500/80 hover:bg-red-600 cursor-not-allowed"}`}
                        onClick={() => {
                          if (!canDeliver) {
                            toast.error(`Paiement incomplet — Solde restant : ${repair.remaining_balance?.toFixed(2)}€`);
                            return;
                          }
                          setDeliveryDialogOpen(true);
                        }}
                        data-testid="deliver-btn"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Livrer au client
                      </Button>
                      {!canDeliver && (
                        <p className="text-xs text-red-400 flex items-center gap-1 px-1">
                          <AlertTriangle className="w-3 h-3" />
                          Solde restant : {repair.remaining_balance?.toFixed(2)}€
                        </p>
                      )}
                    </div>
                  );
                })()}
                {repair.status === "delivered" && (
                  <>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => setReviewDialogOpen(true)}
                      data-testid="add-review-btn"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Ajouter un avis
                    </Button>

                  </>
                )}
              </CardContent>
            </Card>


            {/* ── Avances (Deposits) ── */}
            <Card className="bg-card border-border border-amber-500/30">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-chivo flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-amber-500" />
                    Avances versées
                    {repair.total_deposited > 0 && (
                      <span className="text-sm font-normal text-amber-500 ml-1">
                        ({repair.total_deposited?.toFixed(2)}€)
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => setDepositDialogOpen(true)}
                      className="bg-amber-500 hover:bg-amber-600 text-white h-8">
                      <Plus className="w-3 h-3 mr-1" /> Ajouter
                    </Button>
                    <button onClick={() => setDepositsExpanded(!depositsExpanded)}
                      className="p-1 hover:bg-secondary rounded">
                      {depositsExpanded
                        ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  </div>
                </div>
              </CardHeader>
              {depositsExpanded && (
                <CardContent className="space-y-3 pt-0">
                  {(!repair.deposits || repair.deposits.length === 0) ? (
                    <p className="text-sm text-muted-foreground italic">Aucune avance enregistrée</p>
                  ) : (
                    repair.deposits.map((d) => (
                      <div key={d.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                        <div>
                          <p className="font-medium text-amber-500 text-sm">{d.amount?.toFixed(2)}€</p>
                          <p className="text-xs text-muted-foreground">{d.note}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(d.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                            {d.created_by && ` · ${d.created_by}`}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteDeposit(d.id, d.amount)}
                          className="p-2 hover:bg-destructive/10 rounded-md transition-colors">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </button>
                      </div>
                    ))
                  )}

                  {/* Récap solde */}
                  {repair.invoice_amount && (
                    <div className="border-t border-border pt-3 space-y-1.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total facture</span>
                        <span className="font-medium">{repair.invoice_amount?.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total avancé</span>
                        <span className="text-amber-500 font-medium">− {repair.total_deposited?.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between border-t border-border pt-1.5">
                        <span className="font-semibold">Solde restant</span>
                        <span className={`font-bold text-base ${repair.remaining_balance <= 0 ? "text-emerald-500" : "text-primary"}`}>
                          {repair.remaining_balance <= 0 ? "✓ Soldé" : `${repair.remaining_balance?.toFixed(2)}€`}
                        </span>
                      </div>
                    </div>
                  )}
                  {!repair.invoice_amount && repair.total_deposited > 0 && (
                    <div className="border-t border-border pt-3 flex justify-between text-sm">
                      <span className="text-muted-foreground">Total avancé</span>
                      <span className="text-amber-500 font-semibold">{repair.total_deposited?.toFixed(2)}€</span>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* ══════════════════════════════════════
                 ESPACE DOCUMENTS
               ══════════════════════════════════════ */}
            {(() => {
              // Construire la liste de tous les documents disponibles
              const allDocs = [];

              // 1. Reçu de prise en charge
              if (repair.intake_receipt_pdf) {
                allDocs.push({
                  key: "receipt",
                  label: "Reçu de prise en charge",
                  icon: "📋",
                  color: "blue",
                  date: repair.created_at,
                  badge: null,
                  clientVisible: repair.doc_visibility?.receipt !== false,
                  onOpen: () => {
                    const pdfPath = repair.intake_receipt_pdf.startsWith("/api")
                      ? repair.intake_receipt_pdf
                      : "/api" + repair.intake_receipt_pdf;
                    window.open(process.env.REACT_APP_BACKEND_URL + pdfPath, "_blank");
                  },
                  onDownload: () => {
                    const pdfPath = repair.intake_receipt_pdf.startsWith("/api")
                      ? repair.intake_receipt_pdf
                      : "/api" + repair.intake_receipt_pdf;
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + pdfPath;
                    a.download = `recu-prise-en-charge-${repair.ticket_number}.pdf`;
                    a.click();
                  },
                  onSendEmail: () => handleSendDocumentByEmail(
                    "Reçu de prise en charge",
                    repair.intake_receipt_pdf.startsWith("/api") ? repair.intake_receipt_pdf : "/api" + repair.intake_receipt_pdf,
                    "receipt"
                  ),
                  sendingEmail: sendingEmail === "receipt",
                });
              }

              // 2. Devis et Factures générés
              documents.forEach(doc => {
                allDocs.push({
                  key: doc.id,
                  clientVisible: repair.doc_visibility?.[doc.id] !== false,
                  label: doc.is_quote ? `Devis ${doc.invoice_number}` : `Facture ${doc.invoice_number}`,
                  icon: doc.is_quote ? "📄" : "🧾",
                  color: doc.is_quote ? "amber" : "emerald",
                  date: doc.created_at,
                  amount: doc.total,
                  badge: doc.is_quote
                    ? (doc.client_response === "accept" ? "Accepté" : doc.client_response === "reject" ? "Refusé" : "En attente")
                    : (repair.invoice_paid ? "Payée ✓" : "En attente paiement"),
                  badgeColor: doc.is_quote
                    ? (doc.client_response === "accept" ? "emerald" : doc.client_response === "reject" ? "red" : "amber")
                    : (repair.invoice_paid ? "emerald" : "amber"),
                  onOpen: doc.signed_pdf_path
                    ? () => window.open(process.env.REACT_APP_BACKEND_URL + doc.signed_pdf_path, "_blank")
                    : doc.pdf_path ? () => window.open(process.env.REACT_APP_BACKEND_URL + doc.pdf_path, "_blank") : null,
                  onDownload: doc.pdf_path ? () => {
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + (doc.signed_pdf_path || doc.pdf_path);
                    a.download = `${doc.invoice_number}.pdf`;
                    a.click();
                  } : null,
                  onMarkPaid: (!doc.is_quote && !repair.invoice_paid) ? () => setPaymentDialogOpen(true) : null,
                  onSendEmail: (doc.pdf_path || doc.signed_pdf_path) ? () => handleSendDocumentByEmail(
                    doc.is_quote ? `Devis ${doc.invoice_number}` : `Facture ${doc.invoice_number}`,
                    doc.signed_pdf_path || doc.pdf_path,
                    doc.is_quote ? "quote" : "invoice"
                  ) : null,
                  sendingEmail: sendingEmail === (doc.is_quote ? "quote" : "invoice"),
                  extra: doc.is_quote && doc.client_response ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      doc.client_response === "accept" ? "bg-emerald-500/20 text-emerald-400"
                      : doc.client_response === "reject" ? "bg-red-500/20 text-red-400"
                      : "bg-amber-500/20 text-amber-400"
                    }`}>
                      {doc.client_response === "accept" ? "✓ Accepté" : doc.client_response === "reject" ? "✗ Refusé" : "En attente"}
                      {doc.client_response_date && ` le ${new Date(doc.client_response_date).toLocaleDateString("fr-FR")}`}
                    </span>
                  ) : null
                });
              });

              // 3. Facture PDF uploadée manuellement
              if (repair.invoice_pdf && !documents.some(d => !d.is_quote)) {
                allDocs.push({
                  key: "invoice_pdf",
                  clientVisible: repair.doc_visibility?.invoice_pdf !== false,
                  label: "Facture",
                  icon: "🧾",
                  color: "emerald",
                  date: repair.updated_at,
                  amount: repair.invoice_amount,
                  badge: repair.invoice_paid ? "Payée ✓" : "En attente paiement",
                  badgeColor: repair.invoice_paid ? "emerald" : "amber",
                  onOpen: () => {
                    const pdfPath = repair.invoice_pdf.startsWith("/api") ? repair.invoice_pdf : "/api" + repair.invoice_pdf;
                    window.open(process.env.REACT_APP_BACKEND_URL + pdfPath, "_blank");
                  },
                  onDownload: () => {
                    const pdfPath = repair.invoice_pdf.startsWith("/api") ? repair.invoice_pdf : "/api" + repair.invoice_pdf;
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + pdfPath;
                    a.download = `facture-${repair.ticket_number}.pdf`;
                    a.click();
                  },
                  onMarkPaid: !repair.invoice_paid ? () => setPaymentDialogOpen(true) : null,
                  onSendEmail: () => handleSendDocumentByEmail(
                    "Facture",
                    repair.invoice_pdf.startsWith("/api") ? repair.invoice_pdf : "/api" + repair.invoice_pdf,
                    "invoice_manual"
                  ),
                  sendingEmail: sendingEmail === "invoice_manual",
                });
              }

              // 4. Étiquette Dymo
              if (repair.label_pdf) {
                const labelUrl = repair.label_pdf.startsWith("/api") ? repair.label_pdf : "/api" + repair.label_pdf;
                allDocs.push({
                  key: "label",
                  clientVisible: repair.doc_visibility?.label !== false,
                  label: "Étiquette Dymo",
                  icon: "🏷️",
                  color: "blue",
                  date: repair.label_generated_at || repair.created_at,
                  badge: "QR Code",
                  badgeColor: "blue",
                  onOpen: () => window.open(process.env.REACT_APP_BACKEND_URL + labelUrl, "_blank"),
                  onDownload: () => {
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + labelUrl;
                    a.download = `etiquette-${repair.ticket_number}.pdf`;
                    a.click();
                  },
                  onPrint: () => {
                    const w = window.open(process.env.REACT_APP_BACKEND_URL + labelUrl, "_blank");
                    setTimeout(() => w && w.print(), 800);
                  }
                });
              }

              // 5. Rapport d'intervention
              const reportUrl = repair.report_pdf
                ? (repair.report_pdf.startsWith("/api") ? repair.report_pdf : "/api" + repair.report_pdf)
                : null;
              if (reportUrl || repair.intervention_report_pdf) {
                allDocs.push({
                  key: "rapport",
                  clientVisible: repair.doc_visibility?.rapport !== false,
                  label: "Rapport d'intervention",
                  icon: "🔧",
                  color: "purple",
                  date: repair.report_generated_at || repair.delivered_at || repair.updated_at,
                  badge: repair.delivered_at ? "Signé" : "Brouillon",
                  badgeColor: repair.delivered_at ? "emerald" : "amber",
                  onOpen: reportUrl
                    ? () => window.open(process.env.REACT_APP_BACKEND_URL + reportUrl, "_blank")
                    : () => {
                        try {
                          const bytes = atob(repair.intervention_report_pdf);
                          const arr = new Uint8Array(bytes.length);
                          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
                          const blob = new Blob([arr], { type: "application/pdf" });
                          window.open(URL.createObjectURL(blob), "_blank");
                        } catch (e) { toast.error("Erreur PDF"); }
                      },
                  onDownload: reportUrl
                    ? () => {
                        const a = document.createElement("a");
                        a.href = process.env.REACT_APP_BACKEND_URL + reportUrl;
                        a.download = `rapport-intervention-${repair.ticket_number}.pdf`;
                        a.click();
                      }
                    : null,
                  onSendEmail: reportUrl ? () => handleSendDocumentByEmail(
                    "Rapport d'intervention",
                    reportUrl,
                    "report"
                  ) : null,
                  sendingEmail: sendingEmail === "report",
                  onRegenerate: async () => {
                    try {
                      await repairsAPI.generateReport(id);
                      toast.success("Rapport régénéré");
                      fetchData();
                    } catch (e) { toast.error("Erreur"); }
                  }
                });
              }

              // 6. Photo de livraison
              if (repair.delivery_photo) {
                allDocs.push({
                  key: "delivery_photo",
                  clientVisible: repair.doc_visibility?.delivery_photo !== false,
                  label: "Photo de livraison",
                  icon: "📷",
                  color: "slate",
                  date: repair.delivered_at,
                  onOpen: () => window.open(process.env.REACT_APP_BACKEND_URL + repair.delivery_photo, "_blank"),
                  onDownload: () => {
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + repair.delivery_photo;
                    a.download = `photo-livraison-${repair.ticket_number}.jpg`;
                    a.click();
                  }
                });
              }

              // 7. Signature client
              if (repair.delivery_signature) {
                allDocs.push({
                  key: "signature",
                  clientVisible: repair.doc_visibility?.signature !== false,
                  label: "Signature client",
                  icon: "✍️",
                  color: "slate",
                  date: repair.delivered_at,
                  onOpen: () => window.open(process.env.REACT_APP_BACKEND_URL + repair.delivery_signature, "_blank"),
                  onDownload: () => {
                    const a = document.createElement("a");
                    a.href = process.env.REACT_APP_BACKEND_URL + repair.delivery_signature;
                    a.download = `signature-${repair.ticket_number}.png`;
                    a.click();
                  }
                });
              }

              const COLOR = {
                blue:    { bg: "bg-blue-500/10",    border: "border-blue-500/30",    icon: "text-blue-400",    badge: "bg-blue-500/20 text-blue-400" },
                amber:   { bg: "bg-amber-500/10",   border: "border-amber-500/30",   icon: "text-amber-400",   badge: "bg-amber-500/20 text-amber-400" },
                emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", icon: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-400" },
                purple:  { bg: "bg-purple-500/10",  border: "border-purple-500/30",  icon: "text-purple-400",  badge: "bg-purple-500/20 text-purple-400" },
                red:     { bg: "bg-red-500/10",     border: "border-red-500/30",     icon: "text-red-400",     badge: "bg-red-500/20 text-red-400" },
                slate:   { bg: "bg-slate-500/10",   border: "border-slate-500/30",   icon: "text-slate-400",   badge: "bg-slate-500/20 text-slate-400" },
              };

              return (
                <Card className="bg-card border-border">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between w-full">
                      <CardTitle className="text-lg font-chivo flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        Documents
                        {allDocs.length > 0 && (
                          <span className="ml-1 text-xs font-normal bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                            {allDocs.length}
                          </span>
                        )}
                      </CardTitle>
                      {!repair.invoice_pdf && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                          onClick={handleGenerateInvoice}
                        >
                          <Receipt className="w-3.5 h-3.5 mr-1.5" />
                          Générer facture
                        </Button>
                      )}
                      {!repair.report_pdf && !repair.intervention_report_pdf && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
                          onClick={handleGenerateReport}
                          disabled={generatingReport}
                        >
                          {generatingReport ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <FileText className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {generatingReport ? "Génération..." : "Générer rapport"}
                        </Button>
                      )}
                      {(repair.report_pdf || repair.intervention_report_pdf) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
                          onClick={handleGenerateReport}
                          disabled={generatingReport}
                        >
                          {generatingReport ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          {generatingReport ? "Génération..." : "Regénérer rapport"}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {allDocs.length === 0 ? (
                      <div className="text-center py-8 space-y-2">
                        <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto opacity-40" />
                        <p className="text-sm text-muted-foreground">Aucun document généré</p>
                        <p className="text-xs text-muted-foreground/60">Les documents apparaîtront ici au fil de la réparation</p>
                      </div>
                    ) : (
                      allDocs.map(doc => {
                        const c = COLOR[doc.color] || COLOR.slate;
                        return (
                          <div key={doc.key} className={`rounded-xl border p-3 ${c.bg} ${c.border}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                <span className="text-lg shrink-0">{doc.icon}</span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-foreground">{doc.label}</span>
                                    {doc.badge && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${COLOR[doc.badgeColor]?.badge || c.badge}`}>
                                        {doc.badge}
                                      </span>
                                    )}
                                    {doc.extra}
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    {doc.date && (
                                      <span className="text-xs text-muted-foreground">
                                        {new Date(doc.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                                      </span>
                                    )}
                                    {doc.amount !== undefined && doc.amount !== null && (
                                      <span className="text-xs font-semibold text-foreground">{doc.amount.toFixed(2)}€</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                                {doc.clientVisible !== undefined && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-8 px-2 text-xs ${doc.clientVisible ? "text-emerald-400 hover:bg-emerald-500/10" : "text-muted-foreground hover:bg-white/10"}`}
                                    onClick={() => handleToggleDocVisibility(doc.key, doc.clientVisible)}
                                    title={doc.clientVisible ? "Visible côté client — cliquer pour masquer" : "Masqué côté client — cliquer pour afficher"}
                                  >
                                    {doc.clientVisible
                                      ? <Eye className="w-3.5 h-3.5 mr-1" />
                                      : <EyeOff className="w-3.5 h-3.5 mr-1" />
                                    }
                                    {doc.clientVisible ? "Visible" : "Masqué"}
                                  </Button>
                                )}
                                {doc.onOpen && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`h-8 px-2 text-xs ${c.icon} hover:bg-white/10`}
                                    onClick={doc.onOpen}
                                    title="Ouvrir"
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1" />
                                    Voir
                                  </Button>
                                )}
                                {doc.onDownload && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-xs text-muted-foreground hover:bg-white/10"
                                    onClick={doc.onDownload}
                                    title="Télécharger"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {doc.onPrint && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-xs text-muted-foreground hover:bg-white/10"
                                    onClick={doc.onPrint}
                                    title="Imprimer"
                                  >
                                    <Printer className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {doc.onRegenerate && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-2 text-xs text-muted-foreground hover:bg-white/10"
                                    onClick={doc.onRegenerate}
                                    title="Régénérer"
                                  >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {doc.onMarkPaid && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-3 text-xs bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 font-medium"
                                    onClick={doc.onMarkPaid}
                                    title="Marquer comme payée"
                                  >
                                    <CreditCard className="w-3.5 h-3.5 mr-1" />
                                    Marquer payée
                                  </Button>
                                )}
                                {doc.onSendEmail && repair.client_email && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 px-3 text-xs bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 font-medium"
                                    onClick={doc.onSendEmail}
                                    disabled={doc.sendingEmail}
                                    title={`Envoyer par email à ${repair.client_email}`}
                                  >
                                    {doc.sendingEmail ? (
                                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                    ) : (
                                      <Mail className="w-3.5 h-3.5 mr-1" />
                                    )}
                                    {doc.sendingEmail ? "Envoi..." : "Envoyer"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}

                    {/* Bouton ajouter facture si pas encore de facture */}
                    {!repair.invoice_pdf && !documents.some(d => !d.is_quote) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2 border-dashed text-muted-foreground hover:text-foreground"
                        onClick={() => setInvoicePdfDialogOpen(true)}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Ajouter une facture
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* Photos */}
            {photos.length > 0 && (
              <Card className="bg-card border-border">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-chivo flex items-center gap-2">
                    <Image className="w-5 h-5 text-primary" />
                    Photos ({photos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {photos.map((photo) => (
                      <div 
                        key={photo.id} 
                        className="relative group cursor-pointer"
                        onClick={() => { setSelectedPhoto(photo); setPhotoViewDialogOpen(true); }}
                      >
                        <img 
                          src={process.env.REACT_APP_BACKEND_URL + photo.image_url} 
                          alt={photo.photo_type}
                          className="w-full h-24 object-cover rounded-md"
                        />
                        <div className="absolute top-1 left-1 px-2 py-0.5 bg-black/70 rounded text-xs text-white">
                          {photo.photo_type === "before" ? "Avant" : "Après"}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handlePhotoDelete(photo.id); }}
                          className="absolute top-1 right-1 p-1 bg-red-500/80 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Status History */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-chivo flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  Historique
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {statusHistory.map((entry, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      <div>
                        <StatusBadge status={entry.status} />
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(entry.timestamp).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Messages / Discussion with Client */}
        <Card className="bg-card border-border mt-6">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-chivo flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              Discussion avec le client
              {messages.filter(m => m.sender === "client" && !m.read).length > 0 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  {messages.filter(m => m.sender === "client" && !m.read).length} nouveau(x)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {messages.length === 0 ? (
              <p className="text-muted-foreground text-sm text-center py-4">
                Aucun message pour le moment
              </p>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-3 mb-4" id="messages-container">
                {messages.map(msg => (
                  <div 
                    key={msg.id}
                    className={`p-3 rounded-lg ${
                      msg.sender === "client" 
                        ? "bg-blue-500/10 border border-blue-500/20 mr-8" 
                        : "bg-secondary/50 border border-border ml-8"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">
                        {msg.sender === "client" ? `👤 ${msg.sender_name || "Client"}` : `🔧 ${msg.sender_name || "Vous"}`}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(msg.created_at).toLocaleString("fr-FR", {
                          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
                        })}
                      </span>
                    </div>
                    <p className="text-sm">{msg.message}</p>
                  </div>
                ))}
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
            
            <div className="border-t border-border pt-4">
              <Label className="text-sm mb-2 block">Répondre au client :</Label>
              <Textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Tapez votre réponse... (un email sera envoyé au client)"
                className="bg-secondary/50 min-h-[80px] mb-2"
                disabled={sendingMessage}
              />
              <Button 
                onClick={handleSendMessage}
                disabled={sendingMessage || !newMessage.trim()}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {sendingMessage ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Envoyer la réponse</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-chivo">Changer le statut</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger data-testid="new-status-select" className="bg-secondary/50">
                  <SelectValue placeholder="Sélectionner un statut" />
                </SelectTrigger>
                <SelectContent>
                  {REPAIR_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {getStatusLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleStatusUpdate} 
                  disabled={!newStatus || updating}
                  data-testid="confirm-status-btn"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Parts Selection Dialog - New Component */}
        <PartsSelectionDialog
          open={partsDialogOpen}
          onOpenChange={setPartsDialogOpen}
          repairId={id}
          repair={repair}
          onPartsAdded={fetchData}
          onCreateQuote={(selectedParts) => {
            // Pre-fill invoice items with selected parts
            const items = selectedParts.map(p => ({
              description: p.part_name,
              quantity: p.quantity,
              unit_price: p.unit_price,
              type: "part"
            }));
            // Add mandatory labor item
            items.push({ description: "Main d'œuvre", quantity: 1, unit_price: 0, type: "labor" });
            setInvoiceItems(items);
            setIsQuote(true);
            setInvoiceDialogOpen(true);
          }}
        />

        {/* Invoice Dialog */}
        <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {isQuote ? "Créer un devis" : "Créer une facture"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Parts section */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm font-medium">
                  <Package className="w-4 h-4" />
                  Pièces détachées
                </Label>
                {invoiceItems.filter(item => item.type !== "labor").map((item, idx) => {
                  const realIdx = invoiceItems.indexOf(item);
                  return (
                    <div key={realIdx} className="space-y-2 p-3 bg-secondary/30 rounded-md">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Pièce {idx + 1}</span>
                        <button onClick={() => removeInvoiceItem(realIdx)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </button>
                      </div>
                      <Input
                        placeholder="Description de la pièce"
                        value={item.description}
                        onChange={(e) => updateInvoiceItem(realIdx, "description", e.target.value)}
                        className="bg-secondary/50"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          min="1"
                          placeholder="Qté"
                          value={item.quantity}
                          onChange={(e) => updateInvoiceItem(realIdx, "quantity", e.target.value)}
                          className="bg-secondary/50"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Prix €"
                          value={item.unit_price || ""}
                          onChange={(e) => updateInvoiceItem(realIdx, "unit_price", e.target.value)}
                          className="bg-secondary/50"
                        />
                      </div>
                    </div>
                  );
                })}
                <Button variant="outline" onClick={() => addInvoiceItem("part")} className="w-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une pièce
                </Button>
              </div>
              
              {/* Labor section - Always required */}
              <div className="space-y-2 border-t border-border pt-4">
                <Label className="flex items-center gap-2 text-sm font-medium text-amber-500">
                  <Wrench className="w-4 h-4" />
                  Main d'œuvre (obligatoire)
                </Label>
                {invoiceItems.filter(item => item.type === "labor").map((item) => {
                  const realIdx = invoiceItems.indexOf(item);
                  return (
                    <div key={realIdx} className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-md">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs mb-1 block">Heures</Label>
                          <Input
                            type="number"
                            min="0.5"
                            step="0.5"
                            placeholder="Heures"
                            value={item.quantity}
                            onChange={(e) => updateInvoiceItem(realIdx, "quantity", e.target.value)}
                            className="bg-secondary/50"
                          />
                        </div>
                        <div>
                          <Label className="text-xs mb-1 block">Tarif horaire €</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Tarif/h €"
                            value={item.unit_price || ""}
                            onChange={(e) => updateInvoiceItem(realIdx, "unit_price", e.target.value)}
                            className="bg-secondary/50"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-amber-500/70 mt-2">
                        Total main d'œuvre : {(item.quantity * item.unit_price).toFixed(2)} €
                      </p>
                    </div>
                  );
                })}
                {!invoiceItems.some(item => item.type === "labor") && (
                  <Button variant="outline" onClick={addLaborItem} className="w-full border-amber-500 text-amber-500">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter la main d'œuvre
                  </Button>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Remise (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceDiscount}
                  onChange={(e) => setInvoiceDiscount(e.target.value)}
                  className="bg-secondary/50"
                />
              </div>
              
              <div className="border-t border-border pt-4">
                <div className="flex justify-between text-lg font-medium">
                  <span>Total</span>
                  <span className="text-primary">{calculateInvoiceTotal().toFixed(2)}€</span>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateInvoice} 
                  disabled={updating}
                  data-testid="confirm-invoice-btn"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Créer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Review Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-chivo">Ajouter un avis client</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Note</Label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setReviewRating(star)}
                      data-testid={"rating-star-" + star}
                      className="p-1"
                    >
                      <Star 
                        className={`w-8 h-8 transition-colors duration-150 ${
                          star <= reviewRating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Commentaire (optionnel)</Label>
                <Textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Commentaire du client..."
                  data-testid="review-comment-input"
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setReviewDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleCreateReview} 
                  disabled={updating}
                  data-testid="confirm-review-btn"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Ajouter l'avis
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Device Dialog */}
        <Dialog open={editDeviceDialogOpen} onOpenChange={setEditDeviceDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">Modifier les informations de l'appareil</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type d'appareil</Label>
                  <Select value={editDeviceType} onValueChange={setEditDeviceType}>
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smartphone">Smartphone</SelectItem>
                      <SelectItem value="tablet">Tablette</SelectItem>
                      <SelectItem value="laptop">Ordinateur portable</SelectItem>
                      <SelectItem value="console">Console de jeux</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Marque</Label>
                  <Input
                    value={editDeviceBrand}
                    onChange={(e) => setEditDeviceBrand(e.target.value)}
                    placeholder="Apple, Samsung..."
                    data-testid="edit-brand-input"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Modèle</Label>
                  <Input
                    value={editDeviceModel}
                    onChange={(e) => setEditDeviceModel(e.target.value)}
                    placeholder="iPhone 15, Galaxy S24..."
                    data-testid="edit-model-input"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>N° de série / IMEI</Label>
                  <Input
                    value={editSerialNumber}
                    onChange={(e) => setEditSerialNumber(e.target.value)}
                    placeholder="IMEI / Série"
                    data-testid="edit-serial-input"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description du problème</Label>
                <Textarea
                  value={editProblemDescription}
                  onChange={(e) => setEditProblemDescription(e.target.value)}
                  placeholder="Décrivez le problème..."
                  data-testid="edit-problem-input"
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Coût estimé (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editEstimatedCost}
                  onChange={(e) => setEditEstimatedCost(e.target.value)}
                  placeholder="0.00"
                  data-testid="edit-estimated-cost-input"
                  className="bg-secondary/50 max-w-xs"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditDeviceDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleUpdateDevice} 
                  disabled={updating || !editDeviceBrand || !editDeviceModel || !editProblemDescription}
                  data-testid="confirm-edit-device-btn"
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Client Info Dialog */}
        <Dialog open={editClientDialogOpen} onOpenChange={setEditClientDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Modifier les informations client
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={editClientName.split(" ")[0] || ""}
                    onChange={(e) => {
                      const parts = editClientName.split(" ");
                      const last = parts.slice(1).join(" ");
                      setEditClientName(`${e.target.value} ${last}`.trim());
                    }}
                    placeholder="Jean"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={editClientName.split(" ").slice(1).join(" ") || ""}
                    onChange={(e) => {
                      const first = editClientName.split(" ")[0] || "";
                      setEditClientName(`${first} ${e.target.value}`.trim());
                    }}
                    placeholder="Dupont"
                    className="bg-secondary/50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Téléphone *</Label>
                <Input
                  value={editClientPhone}
                  onChange={(e) => setEditClientPhone(e.target.value)}
                  placeholder="+596 696 00 00 00"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editClientEmail}
                  onChange={(e) => setEditClientEmail(e.target.value)}
                  placeholder="client@email.com"
                  className="bg-secondary/50"
                />
              </div>

              {/* Sync option */}
              <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <input
                  type="checkbox"
                  id="sync-client"
                  checked={editClientSync}
                  onChange={(e) => setEditClientSync(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-primary cursor-pointer"
                />
                <div>
                  <label htmlFor="sync-client" className="text-sm font-medium text-foreground cursor-pointer">
                    Synchroniser avec la fiche client
                  </label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Met à jour le dossier client dans la base de données et applique les modifications à toutes ses réparations.
                  </p>
                </div>
              </div>

              {!editClientSync && (
                <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                  ⚠️ Sans synchronisation, seule cette réparation sera modifiée. La fiche client restera inchangée.
                </p>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditClientDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button
                  onClick={handleUpdateClientInfo}
                  disabled={updating || !editClientName.trim() || !editClientPhone.trim()}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {updating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo Upload Dialog */}
        <Dialog open={photoDialogOpen} onOpenChange={setPhotoDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo">Ajouter une photo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Type de photo</Label>
                <Select value={photoType} onValueChange={setPhotoType}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Avant réparation</SelectItem>
                    <SelectItem value="after">Après réparation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Photo</Label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  ref={fileInputRef}
                  className="hidden"
                />
                {photoPreview ? (
                  <div className="relative">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-md"
                    />
                    <button
                      onClick={() => { setPhotoPreview(null); if(fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                    >
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                  >
                    <Camera className="w-12 h-12 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Cliquez pour sélectionner</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  value={photoDescription}
                  onChange={(e) => setPhotoDescription(e.target.value)}
                  placeholder="Ex: Écran fissuré..."
                  className="bg-secondary/50"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setPhotoDialogOpen(false); setPhotoPreview(null); }} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handlePhotoUpload} 
                  disabled={updating || !photoPreview}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {updating ? "Upload..." : "Ajouter"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Photo View Dialog */}
        <Dialog open={photoViewDialogOpen} onOpenChange={setPhotoViewDialogOpen}>
          <DialogContent className="bg-card border-border max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                Photo {selectedPhoto?.photo_type === "before" ? "avant" : "après"} réparation
              </DialogTitle>
            </DialogHeader>
            {selectedPhoto && (
              <div className="mt-4">
                <img 
                  src={process.env.REACT_APP_BACKEND_URL + selectedPhoto.image_url} 
                  alt={selectedPhoto.photo_type}
                  className="w-full max-h-[60vh] object-contain rounded-md"
                />
                {selectedPhoto.description && (
                  <p className="mt-2 text-sm text-muted-foreground">{selectedPhoto.description}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invoice PDF Upload Dialog */}
        <Dialog open={invoicePdfDialogOpen} onOpenChange={setInvoicePdfDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo">Joindre une facture (Stock Standard)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Uploadez le PDF de la facture générée depuis Stock Standard. Le client recevra un email et pourra la consulter depuis sa page de suivi.
              </p>
              
              <div className="space-y-2">
                <Label>Numéro de facture *</Label>
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Ex: FAC-2024-001"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Montant TTC (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Fichier PDF *</Label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleInvoicePdfSelect}
                  ref={invoiceInputRef}
                  className="hidden"
                />
                {invoicePdfPreview ? (
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm text-foreground">Fichier PDF sélectionné</span>
                    </div>
                    <button
                      onClick={() => { setInvoicePdfPreview(null); if(invoiceInputRef.current) invoiceInputRef.current.value = ""; }}
                      className="p-1 hover:bg-secondary rounded"
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => invoiceInputRef.current?.click()}
                    className="w-full p-6 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                  >
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Cliquez pour sélectionner le PDF</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => { setInvoicePdfDialogOpen(false); setInvoicePdfPreview(null); }} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleInvoicePdfUpload} 
                  disabled={updating || !invoicePdfPreview || !invoiceNumber || !invoiceAmount}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  {updating ? "Upload..." : "Envoyer au client"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delivery Dialog - Photo + Signature + QR Code */}
        <Dialog open={deliveryDialogOpen} onOpenChange={(open) => {
          setDeliveryDialogOpen(open);
          if (!open) {
            setDeliveryPhotoPreview(null);
            setDeliveryPhotos([]);
            setDeliveryNotes("");
            setShowDeliveryQrCode(false);
            setDeliveryQrCodeData(null);
            stopSignaturePolling();
          }
        }}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-500" />
                Livrer la réparation au client
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Pour finaliser la livraison, prenez une photo de l'appareil et faites signer le client.
              </p>
              
              {/* Photos */}
              <div className="space-y-3 border border-emerald-500/30 rounded-lg p-4 bg-emerald-500/5">
                <Label className="flex items-center gap-2 text-emerald-400">
                  <Camera className="w-4 h-4" />
                  Photos de l'appareil réparé *
                </Label>
                
                {/* Photos Preview */}
                {(deliveryPhotoPreview || deliveryPhotos.length > 0) && (
                  <div className="grid grid-cols-3 gap-2">
                    {deliveryPhotoPreview && (
                      <div className="relative group">
                        <img 
                          src={deliveryPhotoPreview} 
                          alt="Aperçu" 
                          className="w-full h-20 object-cover rounded-md"
                        />
                        <button
                          onClick={() => { setDeliveryPhotoPreview(null); if(deliveryPhotoInputRef.current) deliveryPhotoInputRef.current.value = ""; }}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    {deliveryPhotos.map(photo => (
                      <div key={photo.id} className="relative group">
                        <img 
                          src={photo.data} 
                          alt="Photo" 
                          className="w-full h-20 object-cover rounded-md"
                        />
                        <button
                          onClick={() => removeDeliveryPhoto(photo.id)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleDeliveryPhotoSelect}
                  ref={deliveryPhotoInputRef}
                  className="hidden"
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleDeliveryPhotoAdd}
                  id="delivery-photo-multiple"
                  className="hidden"
                />
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => deliveryPhotoInputRef.current?.click()}
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Prendre photo
                  </Button>
                  <label htmlFor="delivery-photo-multiple" className="flex-1">
                    <Button variant="outline" className="w-full" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Importer
                      </span>
                    </Button>
                  </label>
                  <Button 
                    variant="outline"
                    onClick={generateDeliveryQrCode}
                    className="border-primary text-primary"
                  >
                    <QrCode className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* QR Code Display */}
                {showDeliveryQrCode && deliveryQrCodeData && (
                  <div className="mt-3 p-4 bg-white rounded-lg flex flex-col items-center">
                    <QRCodeSVG value={deliveryQrCodeData.upload_url} size={120} />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Scannez pour ajouter des photos depuis votre téléphone
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={refreshDeliveryPhotosFromQr}
                        className="text-xs text-primary border-primary"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Rafraîchir
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setShowDeliveryQrCode(false)}
                        className="text-xs"
                      >
                        Masquer
                      </Button>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {(deliveryPhotoPreview || deliveryPhotos.length > 0)
                    ? `✓ ${deliveryPhotoPreview ? 1 : 0 + deliveryPhotos.length} photo(s) ajoutée(s)`
                    : "⚠️ Au moins une photo est requise"
                  }
                </p>
              </div>

              {/* Signature */}
              <div className="space-y-2">
                <Label className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <PenTool className="w-4 h-4" />
                    Signature du client *
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={generateSignatureQrCode}
                    className="border-primary text-primary text-xs h-7 px-2"
                    title="Faire signer sur le smartphone du client"
                  >
                    <QrCode className="w-3.5 h-3.5 mr-1" />
                    QR code
                  </Button>
                </Label>

                {/* QR Code Signature Display */}
                {showSignatureQrCode && signatureQrData && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex flex-col items-center gap-3">
                    <p className="text-xs text-amber-400 font-medium text-center">
                      📱 Montrez ce QR code au client pour qu'il signe sur son smartphone
                    </p>
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG value={signatureQrData.signature_url} size={140} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {signatureQrPolling ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin text-primary" />
                          <span>En attente de la signature...</span>
                        </>
                      ) : (
                        <span>QR code généré</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={stopSignaturePolling}
                      className="text-xs text-muted-foreground"
                    >
                      Annuler
                    </Button>
                  </div>
                )}

                {/* Affichage selon la source de signature */}
                {mobileSignatureData ? (
                  /* Signature reçue du mobile — affichage image */
                  <div className="border-2 border-emerald-500/50 rounded-lg p-2 bg-white relative">
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full">
                      <CheckCircle2 className="w-3 h-3" /> Signature mobile
                    </div>
                    <img
                      src={mobileSignatureData}
                      alt="Signature client"
                      className="w-full h-40 object-contain"
                    />
                    <button
                      onClick={() => setMobileSignatureData(null)}
                      className="mt-1 text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" /> Effacer et recommencer
                    </button>
                  </div>
                ) : (
                  /* Canvas signature manuelle */
                  <div className="border-2 border-dashed border-border rounded-lg p-2 bg-white">
                    <SignatureCanvas
                      ref={deliverySigCanvasRef}
                      canvasProps={{
                        className: "w-full h-40 cursor-crosshair",
                        style: { width: "100%", height: "160px" }
                      }}
                      backgroundColor="white"
                    />
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {!mobileSignatureData && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearDeliverySignature}
                      className="text-muted-foreground"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Effacer la signature
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground">ou utilisez le QR code →</p>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes de livraison (optionnel)</Label>
                <Textarea
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Observations, état de l'appareil..."
                  className="bg-secondary/50"
                  rows={2}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => { setDeliveryDialogOpen(false); setDeliveryPhotoPreview(null); setDeliveryPhotos([]); setDeliveryNotes(""); setShowDeliveryQrCode(false); }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleDelivery} 
                  disabled={updating || (!deliveryPhotoPreview && deliveryPhotos.length === 0)}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  data-testid="confirm-delivery-btn"
                >
                  {updating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Livraison...</>
                  ) : (
                    <><Package className="w-4 h-4 mr-2" />Confirmer la livraison</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Repair Dialog */}
        <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2 text-amber-500">
                <Ban className="w-5 h-5" />
                Annuler la réparation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-muted-foreground">
                Êtes-vous sûr de vouloir annuler cette réparation ? Cette action changera le statut en &quot;Annulé&quot;.
              </p>
              <div className="space-y-2">
                <Label>Raison de l'annulation (optionnel)</Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Ex: Client ne souhaite plus réparer, pièce introuvable..."
                  className="bg-secondary/50"
                />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelReason(""); }} className="flex-1">
                  Retour
                </Button>
                <Button 
                  onClick={handleCancelRepair} 
                  disabled={updating}
                  className="flex-1 bg-amber-500 hover:bg-amber-600"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Ban className="w-4 h-4 mr-2" />}
                  Confirmer l'annulation
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Repair Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Supprimer la réparation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-muted-foreground">
                <strong className="text-destructive">Attention !</strong> Cette action est irréversible. Toutes les données de cette réparation (photos, documents, historique) seront définitivement supprimées.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleDeleteRepair} 
                  disabled={updating}
                  variant="destructive"
                  className="flex-1"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Supprimer définitivement
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Multiple Photos Upload Dialog */}
        <Dialog open={multiplePhotoDialogOpen} onOpenChange={(open) => { setMultiplePhotoDialogOpen(open); if (!open) setMultiplePhotos([]); }}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Images className="w-5 h-5 text-primary" />
                Ajouter plusieurs photos
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Type de photo</Label>
                <Select value={photoType} onValueChange={setPhotoType}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Avant réparation (prise en charge)</SelectItem>
                    <SelectItem value="after">Après réparation (livraison)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Photos</Label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleMultiplePhotoSelect}
                  ref={multipleFileInputRef}
                  className="hidden"
                />
                
                {/* Photo Grid */}
                {multiplePhotos.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {multiplePhotos.map((photo, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={photo.preview}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          onClick={() => removeMultiplePhoto(index)}
                          className="absolute -top-1 -right-1 p-0.5 bg-destructive rounded-full"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div 
                  onClick={() => multipleFileInputRef.current?.click()}
                  className="w-full h-32 border-2 border-dashed border-border rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors"
                >
                  <Camera className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {multiplePhotos.length > 0 ? "Ajouter d'autres photos" : "Cliquez pour sélectionner"}
                  </p>
                </div>
                
                {multiplePhotos.length > 0 && (
                  <p className="text-center text-sm text-muted-foreground">
                    {multiplePhotos.length} photo(s) sélectionnée(s)
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => { setMultiplePhotoDialogOpen(false); setMultiplePhotos([]); }} 
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleMultiplePhotoUpload} 
                  disabled={updating || multiplePhotos.length === 0}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  {updating ? "Upload..." : `Ajouter ${multiplePhotos.length} photo(s)`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* QR Code Upload Dialog */}
        <Dialog open={qrCodeDialogOpen} onOpenChange={setQrCodeDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <QrCode className="w-5 h-5 text-blue-500" />
                QR Code - Upload mobile
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {qrCodeData && (
                <>
                  <p className="text-center text-muted-foreground">
                    Scannez ce QR code avec votre smartphone pour ajouter des photos à cette réparation.
                  </p>
                  
                  <div className="flex justify-center p-4 bg-white rounded-lg">
                    <QRCodeSVG 
                      value={qrCodeData.upload_url} 
                      size={200}
                      level="M"
                      includeMargin={true}
                    />
                  </div>
                  
                  <div className="text-center space-y-1">
                    <p className="text-sm font-mono text-primary">{repair?.ticket_number}</p>
                    <p className="text-xs text-muted-foreground">
                      Photo: {qrCodeData.photo_type === "before" ? "Avant réparation" : "Après réparation"}
                    </p>
                  </div>
                  
                  <div className="bg-secondary/30 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground text-center">
                      Ce lien expire dans 30 minutes.<br/>
                      Une fois les photos envoyées, cliquez sur "Rafraîchir" pour les voir.
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      className="flex-1 bg-primary"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          const res = await axios.get(
                            `${API_URL}/api/repairs/${id}/photos`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          const photoType = qrCodeData?.photo_type || "before";
                          const mobilePhotos = (res.data || []).filter(p => 
                            p.photo_type === photoType && p.source === "mobile_upload"
                          );
                          if (mobilePhotos.length > 0) {
                            toast.success(`${mobilePhotos.length} photo(s) trouvée(s)! Fermez ce dialogue pour voir les photos.`);
                            fetchData();
                          } else {
                            toast.info("Aucune nouvelle photo trouvée. Vérifiez que l'upload est terminé.");
                          }
                        } catch (err) {
                          toast.error("Erreur lors du rafraîchissement");
                        }
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rafraîchir
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(qrCodeData.upload_url);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copier le lien
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setQrCodeDialogOpen(false)}
                  >
                    Fermer
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Ready Status Dialog with Mandatory Invoice */}
        <Dialog open={readyDialogOpen} onOpenChange={setReadyDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-500" />
                Marquer comme prêt - Facture obligatoire
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Pour passer en statut "Prêt", vous devez joindre la facture. Le client recevra un email l'informant que son appareil est prêt.
              </p>

              <div className="space-y-2">
                <Label>N° de facture *</Label>
                <Input
                  value={readyInvoiceNumber}
                  onChange={(e) => setReadyInvoiceNumber(e.target.value)}
                  placeholder="FAC-2024-001"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Montant TTC (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={readyInvoiceAmount}
                  onChange={(e) => setReadyInvoiceAmount(e.target.value)}
                  placeholder="150.00"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Fichier PDF de la facture *</Label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleReadyInvoiceSelect}
                  ref={readyInvoiceInputRef}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => readyInvoiceInputRef.current?.click()}
                  className="w-full"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {readyInvoicePdf ? "Fichier sélectionné ✓" : "Choisir le fichier PDF"}
                </Button>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setReadyDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleReadyWithInvoice} 
                  disabled={updating || !readyInvoicePdf || !readyInvoiceNumber || !readyInvoiceAmount}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer et marquer prêt"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-500" />
                Marquer la facture comme payée
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Date du paiement</Label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Mode de paiement</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="cash">Espèces</SelectItem>
                    <SelectItem value="card">Carte bancaire</SelectItem>
                    <SelectItem value="wero">Wero</SelectItem>
                    <SelectItem value="transfer">Virement instantané</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="online">Paiement en ligne</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleMarkAsPaid} 
                  disabled={updating}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                >
                  {updating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer le paiement"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* ── Dialog Ajout Avance ── */}
        <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Banknote className="w-5 h-5 text-amber-500" />
                Ajouter une avance
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label>Montant (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="0.00"
                  className="bg-secondary/50 text-lg font-semibold"
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label>Note (optionnel)</Label>
                <Input
                  value={depositNote}
                  onChange={(e) => setDepositNote(e.target.value)}
                  placeholder="Ex: Acompte espèces"
                  className="bg-secondary/50"
                />
              </div>
              {repair?.invoice_amount && depositAmount && parseFloat(depositAmount) > 0 && (
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Déjà avancé</span>
                    <span>{repair.total_deposited?.toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Ce versement</span>
                    <span>+ {parseFloat(depositAmount || 0).toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-border pt-1">
                    <span>Solde après</span>
                    <span className={
                      repair.invoice_amount - repair.total_deposited - parseFloat(depositAmount || 0) <= 0
                        ? "text-emerald-500" : "text-primary"
                    }>
                      {Math.max(0, repair.invoice_amount - repair.total_deposited - parseFloat(depositAmount || 0)).toFixed(2)}€
                    </span>
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setDepositDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button
                  onClick={handleAddDeposit}
                  disabled={depositSubmitting || !depositAmount || parseFloat(depositAmount) <= 0}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {depositSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
